import { ref } from 'vue'
import { showToast } from 'vant'
import { useI18n } from 'vue-i18n'
import { apiGetRecommendedFeed } from '@/apis/theater'
import type { IResRecommendedTheaterItem } from '@/types/apis/theater'
import { useUserStore } from '@/stores/user'
import { useLikeStore } from '@/stores/like'
import { useCollectStore } from '@/stores/collect'
import { formattedVideoItem, type IRefVideoItem } from '@/components/feed-player/data-adapter'

const MAX_CACHED_ITEMS = 1000
const LOAD_MORE_THRESHOLD = 3

export type RecommendFeedItem = IResRecommendedTheaterItem & { RequiresLogin?: boolean }

export function useRecommendFeed() {
  const { t } = useI18n()
  const userStore = useUserStore()
  const likeStore = useLikeStore()
  const collectStore = useCollectStore()

  const items = ref<RecommendFeedItem[]>([])
  const loading = ref(false)
  const hasMore = ref(true)
  const loaded = ref(false)
  const currentIndex = ref(0)

  // 播放器格式化后的数据
  const formattedItems = ref<IRefVideoItem[]>([])

  const updateFormattedItems = () => {
    formattedItems.value = items.value.map(item => formattedVideoItem(item, likeStore, collectStore))
  }

  const reset = () => {
    items.value = []
    loading.value = false
    hasMore.value = true
    loaded.value = false
    currentIndex.value = 0
    formattedItems.value = []
  }

  const loadMore = async (playerRef?: { jumpToIndex: (index: number) => void }) => {
    if (loading.value || !hasMore.value) return

    try {
      loading.value = true

      const lastEpisodeId =
        items.value.length > 0 ? items.value[items.value.length - 1]?.Id || null : null

      const response = await apiGetRecommendedFeed({
        PageSize: 10,
        LastEpisodeId: lastEpisodeId,
      })

      if (response.IsSuccess && response.Body) {
        const newItems = response.Body

        if (newItems.length === 0) {
          hasMore.value = false
          loaded.value = true
          return
        }

        const currentIdx = Number(currentIndex.value ?? 0)

        items.value.push(...newItems)
        loaded.value = true

        if (!userStore.isLogin) {
          hasMore.value = false
          const lastItem = items.value[items.value.length - 1]
          if (lastItem)
            items.value.splice(items.value.length - 1, 1, {
              ...lastItem,
              RequiresLogin: true,
            })
        }

        updateFormattedItems()

        if (items.value.length > MAX_CACHED_ITEMS) {
          const keepFromIndex = Math.max(0, currentIdx - 10)
          items.value = items.value.slice(keepFromIndex)

          const newIndex = Math.max(0, currentIdx - keepFromIndex)
          currentIndex.value = newIndex
          playerRef?.jumpToIndex(newIndex)
          updateFormattedItems()
        }
      } else {
        showToast(t('home.errors.loadFailed'))
      }
    } catch (error) {
      console.error('加载推荐视频失败:', error)
      showToast(t('common.errors.networkError'))
    } finally {
      loading.value = false
    }
  }

  const handleIndexChange = (index: number, playerRef?: { jumpToIndex: (index: number) => void }) => {
    currentIndex.value = index
    const remaining = items.value.length - index
    if (remaining <= LOAD_MORE_THRESHOLD && !loading.value && hasMore.value) {
      void loadMore(playerRef)
    }
  }

  return {
    items,
    formattedItems,
    loading,
    hasMore,
    loaded,
    currentIndex,
    loadMore,
    reset,
    handleIndexChange,
  }
}
